(() => {
  if (window.avrelonHeroCarouselInitialized) return;
  window.avrelonHeroCarouselInitialized = true;

  const selector = '[data-hero-carousel]';
  const instances = new Map();

  class HeroCarousel {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
      this.controls = root.querySelector('[data-hero-controls]');
      this.dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
      this.toggle = root.querySelector('[data-hero-toggle]');
      this.status = root.querySelector('[data-hero-status]');
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.autoplay = root.dataset.autoplay === 'true';
      this.interval = Math.min(12000, Math.max(4000, Number(root.dataset.interval) || 6000));
      this.userPaused = false;
      this.pauseReasons = new Set();
      this.listeners = [];
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.timer = null;

      this.show(this.index);
      if (this.slides.length < 2 || !this.controls) return;

      this.listen(root.querySelector('[data-hero-previous]'), 'click', () => this.navigate(this.index - 1));
      this.listen(root.querySelector('[data-hero-next]'), 'click', () => this.navigate(this.index + 1));
      this.dots.forEach(dot => this.listen(dot, 'click', () => this.navigate(Number(dot.dataset.heroDot))));
      this.listen(this.toggle, 'click', () => {
        this.userPaused = !this.userPaused;
        this.updateToggle();
        this.schedule();
      });
      this.listen(root, 'mouseenter', () => this.pause('hover', true));
      this.listen(root, 'mouseleave', () => this.pause('hover', false));
      this.listen(root, 'focusin', () => this.pause('focus', true));
      this.listen(root, 'focusout', event => {
        if (!root.contains(event.relatedTarget)) this.pause('focus', false);
      });
      this.listen(this.controls, 'keydown', event => {
        const destinations = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (Object.prototype.hasOwnProperty.call(destinations, event.key)) {
          event.preventDefault();
          this.navigate(destinations[event.key]);
        }
      });
      this.listen(document, 'visibilitychange', () => this.pause('hidden', document.hidden));

      const motionChanged = () => {
        this.updateToggle();
        this.schedule();
      };
      if (this.motion.addEventListener) {
        this.listen(this.motion, 'change', motionChanged);
      } else {
        this.motion.addListener(motionChanged);
        this.removeMotionListener = () => this.motion.removeListener(motionChanged);
      }

      if (document.hidden) this.pauseReasons.add('hidden');
      if (root.contains(document.activeElement)) this.pauseReasons.add('focus');
      if (root.matches(':hover')) this.pauseReasons.add('hover');
      if ('IntersectionObserver' in window) {
        this.pauseReasons.add('offscreen');
        this.observer = new IntersectionObserver(entries => {
          const entry = entries.find(candidate => candidate.target === root);
          if (entry) this.pause('offscreen', !entry.isIntersecting || entry.intersectionRatio < 0.1);
        }, { threshold: [0, 0.1] });
        this.observer.observe(root);
      } else {
        const checkViewport = () => {
          const rect = root.getBoundingClientRect();
          this.pause('offscreen', rect.bottom <= 0 || rect.top >= window.innerHeight);
        };
        this.listen(window, 'scroll', checkViewport, { passive: true });
        this.listen(window, 'resize', checkViewport, { passive: true });
        checkViewport();
      }

      this.controls.hidden = false;
      this.updateToggle();
      this.schedule();
    }

    listen(target, type, callback, options) {
      if (!target) return;
      target.addEventListener(type, callback, options);
      this.listeners.push(() => target.removeEventListener(type, callback, options));
    }

    show(index, announce = false) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, position) => {
        if (position === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (announce && this.status) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
    }

    navigate(index) {
      this.show(index, true);
      this.schedule();
    }

    pause(reason, paused) {
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);
      this.schedule();
    }

    updateToggle() {
      if (!this.toggle) return;
      this.toggle.hidden = !this.autoplay || this.motion.matches;
      this.toggle.dataset.paused = String(this.userPaused);
      this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    }

    schedule() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (this.slides.length < 2 || !this.autoplay || this.userPaused || this.motion.matches || this.pauseReasons.size) return;
      this.timer = window.setTimeout(() => {
        this.show(this.index + 1);
        this.schedule();
      }, this.interval);
    }

    destroy() {
      window.clearTimeout(this.timer);
      this.listeners.forEach(remove => remove());
      this.observer?.disconnect();
      this.removeMotionListener?.();
      if (this.controls) this.controls.hidden = true;
    }
  }

  function init(scope) {
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, new HeroCarousel(root));
    });
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((carousel, root) => {
      if (event.target.contains(root) || root.dataset.sectionId === event.detail?.sectionId) {
        carousel.destroy();
        instances.delete(root);
      }
    });
  });
  document.addEventListener('shopify:block:select', event => {
    instances.forEach(carousel => {
      const index = carousel.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
      if (index < 0) return;
      carousel.pause('editor', true);
      carousel.show(index);
    });
  });
  document.addEventListener('shopify:block:deselect', event => {
    instances.forEach(carousel => {
      if (carousel.slides.some(slide => slide.dataset.blockId === event.detail?.blockId)) carousel.pause('editor', false);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
